import { isEmpty } from 'lodash-es';

import { StockExchangeCodeType } from '../../models/fundamental/types';
import { InstrumentSnapshotMap } from '../../store/market-data/types';
import InitialSymbolsDataLoadingStatusCache from '../../store-util/InitialSymbolsDataLoadingStatusCache';
import { updateInstrumentSnapshotCache } from '../../store-util/InstrumentSnapshotCache';
import { updateNBBOPricesCache } from '../../store-util/NBBOPriceCache';
import { updateStatsCache } from '../../store-util/StatsCache';
import { updateTradePriceCacheFromInstrumentSnapshot } from '../../store-util/TradePriceCache';
import { parseBase64String, parseRows } from '../../util/GatewayHelpers';
import { dataArrayToMap } from '../../util/MarketDataHelpers';
import { WebSocketEventData } from '../custom-event/types';
import { LoggerService } from '../logger/Logger.service';
import { GatewayRequestType, GatewayResponseType } from '../web-socket/types';

import { BaseSubscriptionService } from './BaseSubscription.service';
import { SymbolInstrumentSnapshotSubscriptionRequestType } from './types';

const LOG_PREFIX = '[IS-SUBS]';
const logger = LoggerService.getLogger();

/**
 * Instrument Snapshot Subscription Service
 */
export class ISSubscriptionService extends BaseSubscriptionService<SymbolInstrumentSnapshotSubscriptionRequestType> {
  private static instance: ISSubscriptionService;

  // Make the constructor private, to have a single instance
  // eslint-disable-next-line no-useless-constructor
  private constructor() {
    super();
  }

  static getInstance(): ISSubscriptionService {
    if (!this.instance) {
      this.instance = new ISSubscriptionService();
    }

    return this.instance;
  }

  // TODO: HW-1162  Cover all cases with unittesting
  public handleResponseEvent(event: WebSocketEventData<any>): void {
    const data = event.data ?? {};
    const { messageType: messageName, traceIdentifier, snapshots: snapshotsRaw } = data;
    const messageType: GatewayResponseType = GatewayResponseType[messageName] as GatewayResponseType ?? messageName;
    try {
      const snapshots = parseBase64String(snapshotsRaw);
      const parsedData = parseRows(snapshots?.rows ?? [], snapshots) ?? [];
      const finalData = dataArrayToMap(parsedData);
      const traceIdentifierRequest = (this.gatewayService.getRequestedObjectList().find(
        item => item.messageType === this.getSubscriptionRequestType()
          && item.traceIdentifier === traceIdentifier,
      ) ?? {}) as SymbolInstrumentSnapshotSubscriptionRequestType;
      const symbols = traceIdentifierRequest.symbols ?? [];

      const symbolsReceived = Object.keys(finalData);

      if (symbols.length > 0 && symbolsReceived?.length !== symbols.length) {
        const diff: string[] = [];
        symbols.forEach(current => {
          if (!symbolsReceived.includes(current)) {
            diff.push(current);
          }
        });
        logger.warn(`${LOG_PREFIX} handleGWWebsocketMessages - symbols count for InstrumentSnapshot request and response differs - request:${symbols.length} response:${symbolsReceived?.length} -- difference: ${diff}`, {
          symbols: symbols.concat().sort(),
          symbolsReceived,
          diff,
        });
      }

      InitialSymbolsDataLoadingStatusCache.updateStatus('instrumentSnapshot', true);
      updateInstrumentSnapshotCache(finalData as InstrumentSnapshotMap, traceIdentifier);
      updateStatsCache(finalData as InstrumentSnapshotMap);
      updateTradePriceCacheFromInstrumentSnapshot(finalData as InstrumentSnapshotMap);
      updateNBBOPricesCache(finalData as InstrumentSnapshotMap, 'instr');
    } catch (error) {
      logger.error(`${LOG_PREFIX} handleGWWebsocketMessages - error parsing '${messageType}' - ${error}`);
    }
  }

  public subscribe(
    symbol: string,
    stockExchangeCode: StockExchangeCodeType,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(stockExchangeCode);

    if (!requestByBucketAndExchange) {
      const request: SymbolInstrumentSnapshotSubscriptionRequestType = {
        symbols: [ symbol ],
        stockExchangeCode,
        includePreMarket: true,
        includePostMarket: true,
        ...this.getDefaultRequestParams(),
      };
      this.subscripitonRequests.push(request);
      this.gatewayService.sendRequest(request);
      return;
    }

    if (requestByBucketAndExchange.symbols.includes(symbol)) { // This symbol already in the subscription request list!
      return;
    }

    requestByBucketAndExchange.symbols.push(symbol); // add symbol to related list
    this.gatewayService.sendRequest({
      symbols: [ symbol ],
      stockExchangeCode,
      includePreMarket: true,
      includePostMarket: true,
      ...this.getDefaultRequestParams(),
    });
  }

  public subscribeList(
    symbols: string[],
    stockExchangeCode: StockExchangeCodeType,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(stockExchangeCode);

    if (!requestByBucketAndExchange) {
      const request = {
        symbols,
        stockExchangeCode,
        includePreMarket: true,
        includePostMarket: true,
        ...this.getDefaultRequestParams(),
      } as SymbolInstrumentSnapshotSubscriptionRequestType;
      this.subscripitonRequests.push(request);
      this.gatewayService.sendRequest(request);
      return;
    }

    const notRequestedList = symbols.filter(item => !requestByBucketAndExchange?.symbols.includes(item)) || [];

    if (isEmpty(notRequestedList)) { // All symbols are already added to subscription request list!
      return;
    }

    requestByBucketAndExchange.symbols = [ ...requestByBucketAndExchange?.symbols || [], ...notRequestedList ]; // add symbols to related list
    this.gatewayService.sendRequest({
      symbols: notRequestedList,
      stockExchangeCode,
      includePreMarket: true,
      includePostMarket: true,
      ...this.getDefaultRequestParams(),
    });
  }

  public unsubscribe(
    symbol: string,
    stockExchangeCode: StockExchangeCodeType,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(stockExchangeCode);

    if (!requestByBucketAndExchange) { // There is no request in the list by the given exchange!
      return;
    }

    if (!requestByBucketAndExchange.symbols.includes(symbol)) { // There is no request in the list by the given symbol!
      return;
    }

    if (this.shouldDebounce(requestByBucketAndExchange)) {
      return;
    }

    requestByBucketAndExchange.symbols = requestByBucketAndExchange.symbols.filter(item => item !== symbol); // remove only given symbol from the list
    this.gatewayService.sendRequest({
      ...this.getDefaultRequestParams(),
      includePreMarket: true,
      includePostMarket: true,
      symbols: [ symbol ],
      stockExchangeCode,
      messageType: this.getUnsubscriptionRequestType(),
    });
  }

  public unsubscribeList(
    symbols: string[],
    stockExchangeCode: StockExchangeCodeType,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(stockExchangeCode);

    if (!requestByBucketAndExchange) { // There is no request in the list by the given exchange!
      return;
    }

    if (this.shouldDebounce(requestByBucketAndExchange)) {
      return;
    }

    const requestedList = symbols.filter(item => requestByBucketAndExchange?.symbols.includes(item)) || [];

    if (isEmpty(requestedList)) { // The symbols are not in the subscription request list!
      return;
    }

    requestByBucketAndExchange.symbols = requestByBucketAndExchange.symbols.filter(
      item => !requestedList.includes(item),
    ); // remove given symbols from the list

    this.gatewayService.sendRequest({
      ...this.getDefaultRequestParams(),
      symbols: requestedList,
      stockExchangeCode,
      includePreMarket: true,
      includePostMarket: true,
      messageType: this.getUnsubscriptionRequestType(),
    });
  }

  protected getResponseType = (): GatewayResponseType => GatewayResponseType.InstrumentSnapshot;

  protected getSubscriptionRequestType = (): GatewayRequestType => GatewayRequestType.InstrumentSnapshot;

  protected getUnsubscriptionRequestType = (): GatewayRequestType => GatewayRequestType.None;

  private getRequestByBucketAndExchange(
    stockExchangeCode: StockExchangeCodeType,
  ): SymbolInstrumentSnapshotSubscriptionRequestType | undefined {
    return this.subscripitonRequests.find(
      item => item.stockExchangeCode === stockExchangeCode,
    );
  }
}
